/* eslint-disable */
/*
Nexus
Copyright (c) 2012-2020, Visual Computing Lab, ISTI - CNR
All rights reserved.

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE.
*/

import * as THREE from "three";
import {Nexus} from "./nexus.js";
function nocenter() { throw "Centering and in general applying matrix to geometry is unsupported."; }

export class NexusObject extends THREE.Mesh {
    constructor(url, onLoad, onUpdate, renderer, material, scene, camera) {

        var geometry = new THREE.BufferGeometry();
        geometry.center = nocenter;
        var positions = new Float32Array(3);
        geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));

        super(geometry, material); //THREE.Mesh.call( this, geometry, material);
        
        if(onload !== null && typeof(onLoad) == "object")
            throw "NexusObject constructor has been changed.";

        var gl = renderer.getContext();
        this.gl = gl;
        this.isNexusObject = true;
        /*
        function() { 
                        var s = 1/instance.mesh.sphere.radius;
                        var pos = instance.mesh.sphere.center;
                        mesh.position.set(-pos[0]*s, -pos[1]*s, -pos[2]*s);
                        mesh.scale.set(s, s, s); 
        };
        */

        this.frustumCulled = false;

        var mesh = this;
        var instance = this.geometry.instance = new Nexus.Instance(gl);
        instance.open(url);
        instance.onLoad = function() {
            var c = instance.mesh.sphere.center;
            var center = new THREE.Vector3(c[0], c[1], c[2]);
            var radius = instance.mesh.sphere.radius;

            geometry.boundingSphere = new THREE.Sphere(center, radius);
            geometry.boundingBox = mesh.computeBoundingBox();

            if(this.mesh.vertex.normal) {
                var normals = new Float32Array(3);
                geometry.setAttribute( "normal", new THREE.BufferAttribute(normals, 3));
            }
            let materialType = (this.mesh.vertex.normal && !this.mesh.vertex.texCoord)
                ? THREE.MeshLambertMaterial
                : THREE.MeshBasicMaterial;

            if(this.mesh.vertex.color && this.mesh.vertex.texCoord) {
                var uv = new Float32Array(2);
                var colors = new Float32Array(4);
                geometry.setAttribute( "uv", new THREE.BufferAttribute(uv, 2));
                geometry.setAttribute( "color", new THREE.BufferAttribute(colors, 4));
                var texture = new THREE.DataTexture( new Uint8Array([1, 1, 1]), 1, 1, THREE.RGBFormat );
                texture.needsUpdate = true;
                mesh.material = new materialType( { vertexColors: THREE.VertexColors, map: texture } );
            }
            else if(this.mesh.vertex.color) {
                var colors = new Float32Array(4);
                geometry.setAttribute( "color", new THREE.BufferAttribute(colors, 4));
                mesh.material = new materialType({ vertexColors: THREE.VertexColors });
            }
            else if(this.mesh.vertex.texCoord) {
                var uv = new Float32Array(2);
                geometry.setAttribute( "uv", new THREE.BufferAttribute(uv, 2));
                var texture = new THREE.DataTexture( new Uint8Array([1, 1, 1]), 1, 1, THREE.RGBFormat );
                texture.needsUpdate = true;
                mesh.material = new materialType( { color: 0xffffff, map: texture } );
            }

            //this seems not to be needed to setup the attributes and shaders
    /*
            if(this.mesh.face.index) {
                var indices = new Uint32Array(3);
                geometry.setIndex(new THREE.BufferAttribute( indices, 3) );
            }
    */
            if(onLoad) onLoad(mesh);
        };
        instance.onUpdate = function() { onUpdate(this) };

        this.onAfterRender = onAfterRender;
        this.scene = scene;
        this.camera = camera;
    }
}

function onAfterRender(renderer, scene, camera, geometry, material, group) {
	var gl = renderer.getContext();
	var instance = geometry.instance;
	if(!instance || !instance.isReady) return;

	// Skip if mesh is not visible
	if (!this.visible) return;

	var program = gl.getParameter(gl.CURRENT_PROGRAM);
	if (!program) {
		// No valid shader program, skip rendering
		return;
	}

	var s = new THREE.Vector2();
	renderer.getSize(s);
	instance.updateView([0, 0, s.width, s.height],
	camera.projectionMatrix.elements,
	this.modelViewMatrix.elements);

	var attr = instance.attributes;
	attr.position = gl.getAttribLocation(program, "position");
	attr.normal   = gl.getAttribLocation(program, "normal");
	attr.color    = gl.getAttribLocation(program, "color");
	attr.uv       = gl.getAttribLocation(program, "uv");
	attr.size     = gl.getUniformLocation(program, "size");
	attr.scale    = gl.getUniformLocation(program, "scale");
	let map_location = gl.getUniformLocation(program, "map")
	attr.map      = map_location ? gl.getUniform(program, map_location) : null;


	//hack to detect if threejs using point or triangle shaders
	if(instance.mesh.face.index)
		instance.mode = material.isPointsMaterial? "POINT" : "FILL";

	if(attr.size != -1)
		instance.pointsize = material.size;

	//can"t find docs or code on how material.scale is computed in threejs.
	if(attr.scale != -1)
		instance.pointscale = 2.0;

	const wasCullEnabled = gl.isEnabled(gl.CULL_FACE);
	let previousCullFace = null;
	if (wasCullEnabled) {
		previousCullFace = gl.getParameter(gl.CULL_FACE_MODE);
		gl.disable(gl.CULL_FACE);
	}

	try {
		instance.render();
	} finally {
		// Always restore GL state, even if render() throws
		if (wasCullEnabled) {
			gl.enable(gl.CULL_FACE);
			if (previousCullFace !== null) {
				gl.cullFace(previousCullFace);
			}
		}
	}
	Nexus.updateCache(gl);

	// Process diff colors for newly loaded nodes
	if (this._diffColorState && !this._diffColorState.isDisposed) {
		const diffInst = this._diffColorState.diffInst;
		if (diffInst && instance.viewport && instance.projectionMatrix && instance.modelView) {
			diffInst.updateView(instance.viewport, instance.projectionMatrix, instance.modelView);
			diffInst.traversal();
		}
		processDiffColors(this._diffColorState);
	}
}


Object.setPrototypeOf(NexusObject.prototype, THREE.Mesh.prototype);

NexusObject.prototype.dispose = function() {
	var instance = this.geometry.instance;
	var context = instance.context;
	var mesh = instance.mesh;
	Nexus.flush(context, mesh);
	for( var i = 0; i < context.meshes.length; i++) {
		if ( context.meshes[i] === mesh) {
			context.meshes.splice(i, 1); 
			break;
		}
	}
	this.geometry.instance = null;
	this.geometry.dispose();
}

NexusObject.prototype.flush = function() {
	var instance = this.geometry.instance;
	var context = instance.context;
	var mesh = instance.mesh;
	Nexus.flush(context, mesh);
}

NexusObject.prototype.georef = function(url) {
	var n = this;
	var obj = new XMLHttpRequest();
	obj.overrideMimeType("application/json");
	obj.open("GET", url, true);
	obj.onreadystatechange = function () {
		if (obj.readyState == 4 && obj.status == "200") {
			this.georef = JSON.parse(obj.responseText);
			var o = this.georef.origin;
			n.position.set(o[0], o[1], o[2]);
		}
	};
	obj.send(null);  
}

NexusObject.prototype.computeBoundingBox = function() {
	var instance = this.geometry.instance;
	var nexus = instance.mesh;
	if(!nexus.sphere) return;


	var min = new THREE.Vector3( + Infinity, + Infinity, + Infinity );
	var max = new THREE.Vector3( - Infinity, - Infinity, - Infinity );

	var array = new Float32Array(nexus.sink-1);
	//check last level of spheres
	var count = 0;
	for(var i = 0; i < nexus.sink; i++) {
		var patch = nexus.nfirstpatch[i];
		if(nexus.patches[patch*3] != nexus.sink)
			continue;
		var x = nexus.nspheres[i*5];
		var y = nexus.nspheres[i*5+1];
		var z = nexus.nspheres[i*5+2];
		var r = nexus.nspheres[i*5+4]; //tight radius
		if(x-r < min.x) min.x = x-r;
		if(y-r < min.y) min.y = y-r;
		if(z-r < min.z) min.z = z-r;
		if(x-r > max.x) max.x = x+r;
		if(y-r > max.y) max.y = y+r;
		if(z-r > max.z) max.z = z+r;

	}
	return new THREE.Box3(min, max);
}

// RAYCASTING

class NodeCache {
    constructor(maxSize = 100) {
        this.cache = new Map();
        this.maxSize = maxSize;
    }

    get(key) {
        if (!this.cache.has(key)) return null;
        // Refresh item by removing and adding again
        const value = this.cache.get(key);
        this.cache.delete(key);
        this.cache.set(key, value);
        return value;
    }

    set(key, value) {
        if (this.cache.has(key)) {
            this.cache.delete(key);
        } else if (this.cache.size >= this.maxSize) {
            // Remove the first (oldest) item
            this.cache.delete(this.cache.keys().next().value);
        }
        this.cache.set(key, value);
    }
}

const nodeCache = new NodeCache(100);
const pendingRequests = new Map();

NexusObject.prototype.raycastWithInfo = async function(raycaster) {
    // Create local copies instead of using globals
    const localTempMatrix = new THREE.Matrix4();
    const localTempRay = new THREE.Ray();
    const localIntersectionPoint = new THREE.Vector3();
    
    // Cancel any existing operation for THIS SPECIFIC INSTANCE
    if (this._currentRaycastOperation) {
        this._currentRaycastOperation.abort();
    }

    // Create new operation controller for THIS INSTANCE
    const operation = {
        controller: new AbortController(),
        abort() {
            this.controller.abort();
            // Only clear if this is the current operation for this instance
            if (this.parent._currentRaycastOperation === this) {
                this.parent._currentRaycastOperation = null;
            }
        }
    };
    operation.parent = this; // Reference back to the NexusObject instance

    this._currentRaycastOperation = operation;
    const signal = operation.controller.signal;
    const nexus = this.geometry.instance?.mesh;
    if (!nexus || !nexus.sphere) return null;

    // Create an expanded bounding sphere (with a 1.01 multiplier) for early rejection.
    if (!this._boundingSphere) {
        const c = nexus.sphere.center;
        this._boundingSphere = new THREE.Sphere(new THREE.Vector3(c[0], c[1], c[2]), nexus.sphere.radius * 1.01);
    }

    const boundingSphere = this._boundingSphere.clone().applyMatrix4(this.matrixWorld);
    // Transform ray to object space using local variables
    localTempMatrix.copy(this.matrixWorld).invert();
    localTempRay.copy(raycaster.ray).applyMatrix4(localTempMatrix);

    if (!raycaster.ray.intersectSphere(boundingSphere, localIntersectionPoint)) return null;
    if (signal.aborted) return null;

    if (nexus.sink && nexus.basei && (!this._centerPoints || this._centerPoints.length !== nexus.sink * 3)) {
        const centerPoints = new Float32Array(nexus.sink * 3);
        const radii = new Float32Array(nexus.sink);
        const validNodeIndices = [];
        for (let i = 0; i < nexus.sink; i++) {
            const patch = nexus.nfirstpatch[i];
            if (nexus.patches[patch * 3] !== nexus.sink) continue;
            const baseIdx = i * 5;
            const writeIdx = validNodeIndices.length;
            centerPoints[writeIdx * 3] = nexus.nspheres[baseIdx];
            centerPoints[writeIdx * 3 + 1] = nexus.nspheres[baseIdx + 1];
            centerPoints[writeIdx * 3 + 2] = nexus.nspheres[baseIdx + 2];
            radii[writeIdx] = nexus.nspheres[baseIdx + 4];
            validNodeIndices.push(i);
            if (signal.aborted) return null;
        }
        this._centerPoints = centerPoints;
        this._radii = radii;
        this._validNodeIndices = validNodeIndices;
    }
    const validNodeIndices = this._validNodeIndices || [];
    const centerPoints = this._centerPoints || new Float32Array(0);
    const radii = this._radii || new Float32Array(0);

    // Use the optimized arrays to quickly test ray-sphere intersections.
    const intersections = [];
    const ox = localTempRay.origin.x, oy = localTempRay.origin.y, oz = localTempRay.origin.z;
    const dx = localTempRay.direction.x, dy = localTempRay.direction.y, dz = localTempRay.direction.z;
    for (let i = 0; i < validNodeIndices.length; i++) {
        if (signal.aborted) return null;
        const nodeIndex = validNodeIndices[i];
        const cx = centerPoints[i * 3] - ox;
        const cy = centerPoints[i * 3 + 1] - oy;
        const cz = centerPoints[i * 3 + 2] - oz;
        const t = cx * dx + cy * dy + cz * dz;
        if (t < 0) continue; // behind the ray
        const dsq = cx * cx + cy * cy + cz * cz - t * t;
        if (dsq > radii[i] * radii[i]) continue;
        intersections.push({ nodeIndex, distance: t - Math.sqrt(radii[i] * radii[i] - dsq) });
    }
    if (intersections.length === 0) return null;
    intersections.sort((a, b) => a.distance - b.distance);
    if (signal.aborted) return null;

    // Process candidate nodes to find the globally closest hit
    let globalClosest = null; // { point, normal, distance }
    let globalClosestDistance = Infinity;

    // Prefer the last hit node first when available to exploit temporal coherence
    if (this._lastNodeIndex != null) {
        const idx = intersections.findIndex(e => e.nodeIndex === this._lastNodeIndex);
        if (idx > 0) {
            const [entry] = intersections.splice(idx, 1);
            intersections.unshift(entry);
        }
    }

    // Check a limited number of closest nodes; a slightly larger batch smooths results without big cost
    const nodesToCheck = Math.min(12, intersections.length);
    const topNodes = intersections.slice(0, nodesToCheck).map(e => e.nodeIndex);

    // Fetch node data in parallel - PASS THE NEXUS INSTANCE
    const dataList = await Promise.all(topNodes.map(idx => getNodeData(nexus, idx)));

    for (let i = 0; i < dataList.length; i++) {
        if (signal.aborted) return null;
        const nodeIndex = topNodes[i];
        const nodeData = dataList[i];
        if (!nodeData) continue;

        const { hit, distance, normal } = this._findClosestTriangleIntersection(nodeData, localTempRay);
        if (hit && distance < globalClosestDistance) {
            globalClosestDistance = distance;
            globalClosest = { point: hit.clone(), normal: normal ? normal.clone() : null };
            this._lastNodeIndex = nodeIndex; // remember best node for next raycast

            // Early exit if we found a very close hit (optimization)
            if (distance < 0.005) break;
        }
    }

    if (globalClosest) {
        const pointW = globalClosest.point.applyMatrix4(this.matrixWorld);
        let normalW = null;
        if (globalClosest.normal) {
            const normalMatrix = new THREE.Matrix3().getNormalMatrix(this.matrixWorld);
            normalW = globalClosest.normal.clone().applyMatrix3(normalMatrix).normalize();
        }
        return { point: pointW, normal: normalW };
    }

    return null;
};


// Return both the hit point and its true distance (not squared); no per-triangle allocs, caches group AABBs
NexusObject.prototype._findClosestTriangleIntersection = function(nodeData, ray) {
    const { vertices: vert, indices: tri } = nodeData;

    // Create local variables instead of using globals
    const localIntersectionPoint = new THREE.Vector3();
    const localA = new THREE.Vector3();
    const localB = new THREE.Vector3();
    const localC = new THREE.Vector3();
    const localTempEdge1 = new THREE.Vector3();
    const localTempEdge2 = new THREE.Vector3();
    const localTempNormal = new THREE.Vector3();

    // Lazily precompute tight AABBs per triangle group for this node and cache them on nodeData
    const groupSize = nodeData._groupSize || 256; // triangles per group
    let boxes = nodeData._groupBoxes;
    if (!boxes) {
        const triCount = tri.length / 3;
        const groupCount = Math.ceil(triCount / groupSize);
        boxes = new Float32Array(groupCount * 6); // [minx,miny,minz,maxx,maxy,maxz] per group
        for (let g = 0; g < groupCount; g++) {
            let minX = Infinity, minY = Infinity, minZ = Infinity;
            let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
            const startTri = g * groupSize;
            const endTri = Math.min(startTri + groupSize, triCount);
            for (let t = startTri; t < endTri; t++) {
                const base = t * 3;
                const aIndex = tri[base] * 3;
                const bIndex = tri[base + 1] * 3;
                const cIndex = tri[base + 2] * 3;
                // vertex A
                let x = vert[aIndex];
                let y = vert[aIndex + 1];
                let z = vert[aIndex + 2];
                if (x < minX) minX = x; if (y < minY) minY = y; if (z < minZ) minZ = z;
                if (x > maxX) maxX = x; if (y > maxY) maxY = y; if (z > maxZ) maxZ = z;
                // vertex B
                x = vert[bIndex];
                y = vert[bIndex + 1];
                z = vert[bIndex + 2];
                if (x < minX) minX = x; if (y < minY) minY = y; if (z < minZ) minZ = z;
                if (x > maxX) maxX = x; if (y > maxY) maxY = y; if (z > maxZ) maxZ = z;
                // vertex C
                x = vert[cIndex];
                y = vert[cIndex + 1];
                z = vert[cIndex + 2];
                if (x < minX) minX = x; if (y < minY) minY = y; if (z < minZ) minZ = z;
                if (x > maxX) maxX = x; if (y > maxY) maxY = y; if (z > maxZ) maxZ = z;
            }
            const off = g * 6;
            boxes[off] = minX - 0.001; boxes[off + 1] = minY - 0.001; boxes[off + 2] = minZ - 0.001;
            boxes[off + 3] = maxX + 0.001; boxes[off + 4] = maxY + 0.001; boxes[off + 5] = maxZ + 0.001;
        }
        nodeData._groupBoxes = boxes;
        nodeData._groupSize = groupSize;
    }

    let closestDistanceSq = Infinity;
    let closestPoint = null;
    let closestNormal = null;

    const triCount = tri.length / 3;
    const groupCount = Math.ceil(triCount / groupSize);
    const groupBox = new THREE.Box3();

    // Cached values for distance computation
    const rox = ray.origin.x, roy = ray.origin.y, roz = ray.origin.z;

    for (let g = 0; g < groupCount; g++) {
        const off = g * 6;
        groupBox.min.set(boxes[off], boxes[off + 1], boxes[off + 2]);
        groupBox.max.set(boxes[off + 3], boxes[off + 4], boxes[off + 5]);
        if (!ray.intersectsBox(groupBox)) continue;

        const startTri = g * groupSize;
        const endTri = Math.min(startTri + groupSize, triCount);
        for (let t = startTri; t < endTri; t++) {
            const base = t * 3;
            const aIndex = tri[base] * 3;
            const bIndex = tri[base + 1] * 3;
            const cIndex = tri[base + 2] * 3;

            localA.set(vert[aIndex], vert[aIndex + 1], vert[aIndex + 2]);
            localB.set(vert[bIndex], vert[bIndex + 1], vert[bIndex + 2]);
            localC.set(vert[cIndex], vert[cIndex + 1], vert[cIndex + 2]);

            // Prefer front faces first (backface culling = true)
            const hit = ray.intersectTriangle(localA, localB, localC, true, localIntersectionPoint);
            if (!hit) continue;

            const dx = localIntersectionPoint.x - rox;
            const dy = localIntersectionPoint.y - roy;
            const dz = localIntersectionPoint.z - roz;
            const distSq = dx * dx + dy * dy + dz * dz;
            if (distSq < closestDistanceSq) {
                closestDistanceSq = distSq;
                if (!closestPoint) closestPoint = new THREE.Vector3();
                closestPoint.copy(localIntersectionPoint);

                // Compute triangle normal in local space using local temporaries
                localTempEdge1.subVectors(localB, localA);
                localTempEdge2.subVectors(localC, localA);
                localTempNormal.crossVectors(localTempEdge1, localTempEdge2).normalize();
                if (!closestNormal) closestNormal = new THREE.Vector3();
                closestNormal.copy(localTempNormal);
            }
        }
    }

    return {
        hit: closestPoint,
        distance: Number.isFinite(closestDistanceSq) ? Math.sqrt(closestDistanceSq) : Infinity,
        normal: closestNormal
    };
};

// Backward-compatible API returning only the point
NexusObject.prototype.raycast = async function(raycaster) {
    const res = await this.raycastWithInfo(raycaster);
    return res ? res.point : null;
};

// ----- Helper functions for node data retrieval -----

async function getRequestResult(request) {
    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

async function getNodeData(nexus, nodeIndex) {
    // Create a unique cache key that includes the mesh URL to avoid cross-contamination
    const cacheKey = `${nexus.url}_${nodeIndex}`;
    
    // Check cache first
    let nodeData = nodeCache.get(cacheKey);
    if (nodeData) return nodeData;

    // Check if request is already pending for this specific mesh/node combination
    if (pendingRequests.has(cacheKey)) {
        return pendingRequests.get(cacheKey);
    }

    // Create new request
    const promise = (async () => {
        try {
            const data = await fetchNodeData(nexus, nodeIndex);
            nodeCache.set(cacheKey, data);
            return data;
        } finally {
            pendingRequests.delete(cacheKey);
        }
    })();
    pendingRequests.set(cacheKey, promise);
    return promise;
}

async function fetchNodeData(nexus, nodeIndex) {
    // Check if database is initialized
    if (!nexus.db) {
        throw new Error("Database not initialized");
    }
    try {
        let transaction = nexus.db.transaction("mesh", "readwrite");
        const store = transaction.objectStore("mesh");
        
        // Use a unique key for IndexedDB storage that includes the mesh URL
        const dbKey = `${nexus.url}_${nodeIndex}`;
        let node = await getRequestResult(store.get(dbKey));

        // If node not found in IndexedDB, fetch from server
        if (!node) {            
            // Calculate node offset and size
            const start = nexus.noffsets[nodeIndex];
            const end = nexus.noffsets[nodeIndex + 1];
            
            // Make HTTP range request for the node data
            const response = await fetch(nexus.url, {
                headers: {
                    'Range': `bytes=${start}-${end - 1}`
                }
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Get array buffer from response
            node = await response.arrayBuffer();

            // Store for future use with the unique key
            transaction = nexus.db.transaction("mesh", "readwrite");
            transaction.objectStore("mesh").put(node, dbKey);
            transaction.commit();
        }
        
        const nv = nexus.nvertices[nodeIndex];
        const nf = nexus.nfaces[nodeIndex];
        const vertices = new Uint8Array(node, 0, nv * nexus.vsize);
        const view = new Uint8Array(node, 0, nv * nexus.vsize);
        const v = view.subarray(0, nv * 12);
        vertices.set(v);
        let off = nv * 12;
        if (nexus.vertex.texCoord) {
            const uv = view.subarray(off, off + nv * 8);
            vertices.set(uv, off);
            off += nv * 8;
        }
        if (nexus.vertex.normal && nexus.vertex.color) {
            const no = view.subarray(off, off + nv * 6);
            const co = view.subarray(off + nv * 6, off + nv * 6 + nv * 4);
            vertices.set(co, off);
            vertices.set(no, off + nv * 4);
        } else {
            if (nexus.vertex.normal) {
                const no = view.subarray(off, off + nv * 6);
                vertices.set(no, off);
            }
            if (nexus.vertex.color) {
                const co = view.subarray(off, off + nv * 4);
                vertices.set(co, off);
            }
        }
        const nodeVertices = new Float32Array(vertices.buffer, 0, nv * 3);
        const indices = new Uint16Array(node, nv * nexus.vsize, nf * 3);
        return { vertices: nodeVertices, indices: indices };
    } catch (error) {
        console.error("Error getting node data:", error);
        throw error;
    }
}

async function processDiffColors(state) {
  if (!state || state.isDisposed || state.processing) return;

  const targetStatus = state.targetMesh?.status;
  const diffStatus = state.diffNexus?.status;
  if (!targetStatus || !diffStatus) return;

  state.processing = true;
  try {
    state.nexusObject.updateMatrixWorld(true);
    state.diffMesh.updateMatrixWorld(true);

    const targetMatrix = state.nexusObject.matrixWorld;
    const diffMatrix = state.diffMesh.matrixWorld;
    const gridSize = state.gridSize;

    // Build spatial grid from diff mesh
    const grid = state.grid || new Map();
    const tempVec = new THREE.Vector3();

    for (let i = 0; i < diffStatus.length; i++) {
      if (state.isDisposed || diffStatus[i] !== 1 || state.diffCached.has(i)) continue;

      const data = await getNodeData(state.diffNexus, i);
      if (!data) continue;

      const nvDiff = state.diffNexus.nvertices?.[i] || 0;
      for (let v = 0; v < nvDiff; v++) {
        tempVec.set(data.vertices[v * 3], data.vertices[v * 3 + 1], data.vertices[v * 3 + 2]).applyMatrix4(diffMatrix);
        const key = `${Math.floor(tempVec.x / gridSize)},${Math.floor(tempVec.y / gridSize)}`;
        if (!grid.has(key)) grid.set(key, []);
        grid.get(key).push(tempVec.x, tempVec.y, tempVec.z);
      }
      state.diffCached.add(i);
    }
    state.grid = grid;

    // Process target nodes
    const mapper = createColorMapper(state);
    let rangeUpdated = false;

    for (let i = 0; i < targetStatus.length; i++) {
      if (state.isDisposed || targetStatus[i] !== 1 || state.processed.has(i)) continue;

      const data = await getNodeData(state.targetMesh, i);
      if (!data) continue;

      const nvTarget = state.targetMesh.nvertices?.[i] || 0;
      const rawValues = new Float32Array(nvTarget);
      const maxDistSq = gridSize * gridSize * 2;

      for (let v = 0; v < nvTarget; v++) {
        tempVec.set(data.vertices[v * 3], data.vertices[v * 3 + 1], data.vertices[v * 3 + 2]).applyMatrix4(targetMatrix);
        const gx = Math.floor(tempVec.x / gridSize);
        const gy = Math.floor(tempVec.y / gridSize);

        let nearestZ = null;
        let minDistSq = Infinity;

        for (let dx = -1; dx <= 1; dx++) {
          for (let dy = -1; dy <= 1; dy++) {
            const bucket = grid.get(`${gx + dx},${gy + dy}`);
            if (!bucket) continue;
            for (let k = 0; k < bucket.length; k += 3) {
              const distSq = (tempVec.x - bucket[k]) ** 2 + (tempVec.y - bucket[k + 1]) ** 2;
              if (distSq < minDistSq) {
                minDistSq = distSq;
                nearestZ = bucket[k + 2];
              }
            }
          }
        }

        if (nearestZ !== null && minDistSq <= maxDistSq) {
          rawValues[v] = nearestZ;
          if (!state.userRange) {
            const val = state.mode === 'abs' ? Math.abs(nearestZ) : nearestZ;
            if (val < state.autoRange.min) { state.autoRange.min = val; rangeUpdated = true; }
            if (val > state.autoRange.max) { state.autoRange.max = val; rangeUpdated = true; }
          }
        } else {
          rawValues[v] = NaN;
        }
      }

      state.rawValues[i] = rawValues;
      applyColorsToNode(state, i, mapper);
      state.processed.add(i);
    }

    if (rangeUpdated && !state.userRange) {
      const [min, max] = [state.autoRange.min, state.autoRange.max];
      state.range = [isFinite(min) ? min : (state.mode === 'abs' ? 0 : -1), isFinite(max) ? max : 1];
      const newMapper = createColorMapper(state);
      state.processed.forEach(i => applyColorsToNode(state, i, newMapper));
    }
  } finally {
    state.processing = false;
  }
}

function createColorMapper(state) {
  const [min, max] = state.range || (state.mode === 'abs' ? [0, 1] : [-1, 1]);
  const { clamp, alpha, mode } = state;

  const lerp = (c0, c1, t) => {
    const clamped = Math.max(0, Math.min(1, t));
    return c0.map((v, i) => Math.round(v + (c1[i] - v) * clamped));
  };

  return {
    toRGBA(val) {
      if (clamp) val = Math.max(min, Math.min(max, val));

      if (mode === 'signed') {
        if (val >= 0) {
          const t = max > 0 ? val / max : 0;
          const [r, g, b] = lerp([255, 255, 84], [233, 51, 35], t);
          return [r, g, b, alpha];
        }
        const t = min < 0 ? Math.abs(val / min) : 0;
        const [r, g, b] = lerp([117, 251, 253], [0, 0, 245], t);
        return [r, g, b, alpha];
      }

      const t = max > 0 ? Math.max(0, val) / max : 0;
      const [r, g, b] = lerp([255, 255, 84], [233, 51, 35], t);
      return [r, g, b, alpha];
    }
  };
}

function applyColorsToNode(state, nodeIndex, mapper) {
  const rawValues = state.rawValues[nodeIndex];
  if (!rawValues?.length) return;

  const colors = new Uint8Array(rawValues.length * 4);
  const whiteColor = [255, 255, 255, 255];

  for (let i = 0; i < rawValues.length; i++) {
    const val = rawValues[i];
    const color = isFinite(val) ? mapper.toRGBA(state.mode === 'abs' ? Math.abs(val) : val) : whiteColor;
    colors.set(color, i * 4);
  }

  if (!state.targetMesh.colorVBO) state.targetMesh.colorVBO = [];
  if (!state.targetMesh.colorVBO[nodeIndex]) {
    state.targetMesh.colorVBO[nodeIndex] = state.gl.createBuffer();
  }

  state.gl.bindBuffer(state.gl.ARRAY_BUFFER, state.targetMesh.colorVBO[nodeIndex]);
  state.gl.bufferData(state.gl.ARRAY_BUFFER, colors, state.gl.STATIC_DRAW);
}

NexusObject.prototype.applyDiffColors = async function(diffMesh, { range = null, mode = 'signed', alpha = 255, clamp = true } = {}) {
  const targetMesh = this.geometry?.instance?.mesh;
  const diffNexus = diffMesh?.geometry?.instance?.mesh;

  if (!targetMesh || !diffNexus) {
    console.warn('applyDiffColors: meshes not ready');
    return;
  }

  if (this._diffColorState) this._diffColorState.isDisposed = true;

  this._diffColorState = {
    nexusObject: this,
    targetMesh,
    diffMesh,
    diffInst: diffMesh.geometry.instance,
    diffNexus,
    gl: this.gl,
    mode,
    clamp,
    alpha,
    gridSize: 10,
    userRange: range,
    range: range || (mode === 'abs' ? [0, 1] : [-1, 1]),
    autoRange: range ? null : { min: Infinity, max: -Infinity },
    rawValues: [],
    processed: new Set(),
    diffCached: new Set(),
    isDisposed: false,
  };

  if (this.material) {
    this.material.vertexColors = true;
    this.material.needsUpdate = true;
  }
};
